/**
 * ModalityOptions Component
 *
 * This component displays and manages exam modality options that can be toggled by the proctor.
 * It shows a list of available options grouped by category, with toggles to enable/disable each option.
 * When a toggle is changed, it sends an API request to update the option's state and shows a loading spinner
 * during the request.
 */
import {
  Text,
  Card,
  Flex,
  View,
  Alert,
  IconCompleteSolid,
  IconProgressLine,
} from '@smarterservices/smarter-ui';
import { getStore, useExamSessionStore, useAppStore } from '../../../stores/StoreContext';
import React, { useEffect, useState } from 'react';
import { Checkbox, ScreenReaderContent, Spinner } from '@instructure/ui';
import CONSTANTS from '../../../config/constants';
import ButtonWithConfirmation from '@ui/shared/components/Common/ButtonWithConfirmation';
import FragmentLoader from '../../../components/Common/FragmentLoader';
import { randomId } from '../../../helpers/utilities';

const ModalityOptions = (props) => {
  // Get access to the stores
  const ExamSessionStore = getStore('examSessionStore');
  const AppStore = getStore('appStore');
  const [shouldBeLockedDown, setShouldBeLockedDown] = useState(false);

  const isRoomConnected = useAppStore((state) => state.isRoomConnected);
  const journey = useExamSessionStore((state) => state.journey);

  useEffect(() => {
    const hasStep = ExamSessionStore.hasStartedJourneyStep("ExamInProgress");
    if (hasStep) {
      setShouldBeLockedDown(true);
    }
  }, [journey]);

  // State to store the modality option groups fetched from the API
  const [modalityOptionGroups, setModalityOptionGroups] = useState(null);

  // Use the currentModalityOptions from the store to track which options are currently enabled
  const currentModalityOptions = useExamSessionStore(
    (store) => store.currentModalityOptions
  );

  // Options that support force reconnect (kill stream + reconnect)
  const reconnectableOptions = ['recordWebcam', 'recordScreen', 'recordAudio'];

  // Map option IDs to the stream name used by the killStream action
  const streamNameMap = {
    recordWebcam: 'webcam',
    recordScreen: 'screen',
    recordAudio: 'audio',
  };

  /**
   * Force reconnect a stream by invoking the killStream action
   * @param {string} optionId - The modality option ID (e.g. 'recordWebcam')
   */
  const handleForceReconnect = async (optionId) => {
    const streamName = streamNameMap[optionId];
    if (!streamName) return;
    try {
      await ExamSessionStore.invokeAction({ action: 'killStream', streams: [streamName] });
    } catch (error) {
      console.error(`[ModalityOptions] Force reconnect failed for ${streamName}:`, error);
    }
  };

  // Options that cannot be changed by the proctor (read-only)
  // TODO: This should come from the database...
  const readOnlyOptions = [
    'recordWebcam',
    'recordScreen',
    'recordAudio',
    'verifyId',
    'desktopAppCheck',
    'roomScan',
    'secondCamera',
    'closeOpenTabs'
  ];

  // Track which options are currently being updated (for showing spinners)
  const [loadingOptions, setLoadingOptions] = useState([]);

  /**
   * On component mount, load the available modality options from the API
   */
  useEffect(() => {
    async function getModalityOptionGroups() {
      const modalityOptionsResult =
        await ExamSessionStore.loadModalityOptions();
      if (modalityOptionsResult.successful) {
        setModalityOptionGroups(modalityOptionsResult.data);
      }
    }
    getModalityOptionGroups();
  }, []);

  /**
   * Handle toggling a modality option
   *
   * @param {Object} option - The option being toggled
   * @param {boolean} checked - Whether the option should be enabled (true) or disabled (false)
   * @param {Event} event - The DOM event from the checkbox
   */
  const handleModalityOptionChange = async (option, checked, event) => {
    // Ignore programmatic changes
    if (!event.nativeEvent.isTrusted) return;

    try {
      // Add this option to the loading state to show spinner
      setLoadingOptions((prev) => [...prev, option.id]);

      // Determine action type based on the checkbox state
      const action = checked ? 'apply' : 'remove';

      // Prepare payload for the API call
      const modalityOptions = {
        action:
          action === 'apply' ? 'applyModalityOptions' : 'removeModalityOptions',
        modalityOptions: [{ name: option.id }],
      };

      // Send the request to update the modality option
      const modalityOptionChangeResult =
        await ExamSessionStore.invokeAction(modalityOptions);

      if (modalityOptionChangeResult.successful) {
        // If successful, log the action as an annotation
        const payload = {
          installSid: ExamSessionStore.installSid,
          examSid: ExamSessionStore.examSid,
          sessionSid: ExamSessionStore.sessionSid,
          payload: {
            typeId:
              action === 'apply'
                ? CONSTANTS.ANNOTATIONS.TYPES.APPLY_MODALITY_OPTION
                : CONSTANTS.ANNOTATIONS.TYPES.REMOVE_MODALITY_OPTION,
            details: `${option.title} ${action === 'apply' ? 'applied' : 'removed'} successfully`,
            createdById: `Live Proctor`,
            metadata: {
              proctorUserSid: AppStore.identity,
            },
          },
        };

        // Add an event that it was successfully modified
        const annotationResult =
          await AppStore.ApiClient.addAnnotation(payload);

        if (!annotationResult.successful) {
          console.log('Failed to add annotation:', annotationResult);
        }
      } else {
        //TODO: We could add some logging here to catch any errors...
      }
    } catch (error) {
      //TODO: We could add some logging here to catch any errors...
    } finally {
      // Always remove the loading state when done, regardless of success/failure
      setLoadingOptions((prev) => prev.filter((id) => id !== option.id));
    }
  };

  return (
    <Card {...props}>
      <Card.Header
        background="brand"
        padding={CONSTANTS.STYLES.CARD_HEADER_PADDING}
      >
        <Card.Title>
          <Text variant={CONSTANTS.STYLES.CARD_TITLE_VARIANT}>
            Modality Options
          </Text>
        </Card.Title>
      </Card.Header>
      <Card.Body padding={CONSTANTS.STYLES.CARD_BODY_PADDING}>
        <>
          {!shouldBeLockedDown &&
            (
              <Alert variant="warning" transition="none" margin="medium none none none">
                All options will be locked down once the exam is unlocked.
              </Alert>
            )
          }
          {modalityOptionGroups ? (
            // Map through each group of options
            modalityOptionGroups.optionGroups.map((group) => (
              <View key={group.sid} as="div" margin="small none none none">
                {/* Group title */}
                <View as="div" margin="x-small none">
                  <Text variant={CONSTANTS.STYLES.CARD_BODY_TEXT_VARIANT}>
                    <b>{group.title}</b>
                  </Text>
                </View>
                {/* Map through each option in this group */}
                {group.options.map((opt) => {
                  let disabled =
                    readOnlyOptions.includes(opt.id) ||
                    loadingOptions.includes(opt.id) ||
                    !currentModalityOptions?.length;
                  // Determine if this option is currently checked
                  let checked =
                    currentModalityOptions.includes(opt.id) ||
                    !currentModalityOptions?.length;
                  // If this is a readonly option, it should always appear checked
                  if (readOnlyOptions.includes(opt.id)) {
                    checked = true;
                  }

                  return (
                    <React.Fragment key={opt.id}>
                      {/* Only show options that are configured for this exam */}
                      {ExamSessionStore?.modalityOptions?.hasOwnProperty(
                        opt.id
                      ) && (
                          <Flex
                            key={randomId()}
                            as="div"
                            alignItems="center"
                            margin="none none xx-small none"
                          >
                            {/* Toggle control container */}
                            <Flex.Item
                              padding="none none none medium"
                              width="70px"
                              height="28px"
                              justifyContent="center"
                            >
                              <View
                                as="div"
                                width="100%"
                                justifyContent="center"
                                display="inline-block"
                                textAlign="center"
                              >
                                {/* Show either the checkbox or a spinner when loading */}
                                {!loadingOptions.includes(opt.id) ? (
                                  <>
                                    {disabled ? (
                                      <IconCompleteSolid
                                        size="small"
                                        color="success"
                                        margin="none none none medium"
                                        style={{ fontSize: '20px' }}
                                      />
                                    ) : (
                                      <Checkbox
                                        key={opt.id}
                                        label={
                                          <ScreenReaderContent>
                                            {opt.title}
                                          </ScreenReaderContent>
                                        }
                                        value={opt.id}
                                        variant="toggle"
                                        size="small"
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={(event) =>
                                          handleModalityOptionChange(
                                            opt,
                                            !currentModalityOptions.includes(
                                              opt.id
                                            ),
                                            event
                                          )
                                        }
                                      />
                                    )}
                                  </>
                                ) : (
                                  // Loading spinner shown during API calls
                                  <View
                                    as="div"
                                    justifyContent="center"
                                    margin="none none none x-small"
                                  >
                                    <Spinner size="x-small" renderTitle="Loading" />
                                  </View>
                                )}
                              </View>
                            </Flex.Item>
                            {/* Option title */}
                            <Flex.Item shouldGrow>
                              <Text variant="contentSmall">{opt.title}</Text>
                            </Flex.Item>
                            {/* Force Reconnect button for stream options if room is connected... */}
                            {(reconnectableOptions.includes(opt.id) && isRoomConnected) && (
                              <Flex.Item>
                                <ButtonWithConfirmation
                                  variant="icon"
                                  size="small"
                                  color="secondary"
                                  margin="none"
                                  screenReaderLabel="Force Reconnect"
                                  tooltipProps={{ placement: 'end' }}
                                  confirmationText="Disconnect the current stream and force tester to reconnect?"
                                  confirmButtonText="Yes"
                                  cancelButtonText="No"
                                  confirmButtonColor="danger"
                                  onConfirm={() => handleForceReconnect(opt.id)}
                                >
                                  <IconProgressLine />
                                </ButtonWithConfirmation>
                              </Flex.Item>
                            )}
                          </Flex>
                        )}
                    </React.Fragment>
                  );
                })}
              </View>
            ))
          ) : (
            // Show loading indicator while fetching options
            <FragmentLoader title="Loading Modality Options" />
          )}
        </>
      </Card.Body>
    </Card>
  );
};

export default ModalityOptions;
